// Reusable primitives for the backgrounds feature.
// Self-contained styled components so the feature doesn't have to touch the rest of the app's CSS.

import React from 'react';

export const GLASS_STYLE = {
  background: 'rgba(255,255,255,0.72)',
  border: '1px solid rgba(255,255,255,0.6)',
  boxShadow:
    '0 1px 2px rgba(10,10,10,0.04), 0 8px 24px -12px rgba(10,10,10,0.18)',
  backdropFilter: 'blur(18px) saturate(140%)',
  WebkitBackdropFilter: 'blur(18px) saturate(140%)',
};

export const Card = ({ children, style = {}, padding = 20, glass = false }) => (
  <div
    style={{
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: 10,
      padding,
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      position: 'relative',
      ...(glass ? GLASS_STYLE : {}),
      ...style,
    }}
  >
    {children}
  </div>
);

export const PrimaryButton = ({ children, onClick, style = {}, type, disabled }) => (
  <button
    onClick={onClick}
    type={type}
    disabled={disabled}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      padding: '10px 18px',
      borderRadius: 10,
      border: 0,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.55 : 1,
      background: 'linear-gradient(135deg,#ff6900,#f54900)',
      color: '#fff',
      font: '500 14px/1 Inter',
      boxShadow:
        '0 4px 6px -4px rgba(255,105,0,.3),0 10px 15px -3px rgba(255,105,0,.3)',
      ...style,
    }}
  >
    {children}
  </button>
);

export const SecondaryButton = ({ children, onClick, style = {}, type, disabled }) => (
  <button
    onClick={onClick}
    type={type}
    disabled={disabled}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      padding: '9px 14px',
      borderRadius: 8,
      border: '1px solid rgba(0,0,0,0.1)',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.55 : 1,
      background: '#fff',
      color: '#0a0a0a',
      font: '500 14px/1 Inter',
      ...style,
    }}
  >
    {children}
  </button>
);

export const GhostButton = ({ children, onClick, style = {}, type }) => (
  <button
    onClick={onClick}
    type={type}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '8px 10px',
      borderRadius: 8,
      border: 0,
      cursor: 'pointer',
      background: 'transparent',
      color: '#4a5565',
      font: '500 13px/1 Inter',
      ...style,
    }}
  >
    {children}
  </button>
);

export const Chip = ({ children, bg, color, border }) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      padding: '3px 10px',
      borderRadius: 9999,
      background: bg,
      color,
      border: border || 'none',
      font: '500 12px/1.4 Inter',
    }}
  >
    {children}
  </span>
);

export const Label = ({ children, hint }) => (
  <label
    style={{
      display: 'block',
      font: '500 12px/1.4 Inter',
      color: '#364153',
      marginBottom: 6,
    }}
  >
    {children}
    {hint && (
      <span style={{ marginLeft: 6, color: '#99a1af', fontWeight: 400 }}>{hint}</span>
    )}
  </label>
);

export const Input = ({ style = {}, ...p }) => (
  <input
    {...p}
    style={{
      width: '100%',
      padding: '9px 12px',
      border: '1px solid #e5e7eb',
      borderRadius: 8,
      background: '#fff',
      font: '400 13px/1.4 Inter',
      color: '#0a0a0a',
      outline: 'none',
      boxSizing: 'border-box',
      ...style,
    }}
  />
);

export const Select = ({ style = {}, children, ...p }) => (
  <select
    {...p}
    style={{
      width: '100%',
      padding: '9px 12px',
      border: '1px solid #e5e7eb',
      borderRadius: 8,
      background: '#fff',
      font: '400 13px/1.4 Inter',
      color: '#0a0a0a',
      outline: 'none',
      boxSizing: 'border-box',
      appearance: 'none',
      backgroundImage:
        'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%2399a1af\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'><polyline points=\'6 9 12 15 18 9\'/></svg>")',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'right 10px center',
      paddingRight: 30,
      ...style,
    }}
  >
    {children}
  </select>
);

export const Slider = ({ value, onChange, min = 0, max = 100, step = 1, style = {} }) => (
  <input
    type="range"
    value={value}
    min={min}
    max={max}
    step={step}
    onChange={(e) => onChange(Number(e.target.value))}
    style={{ width: '100%', accentColor: '#f54900', ...style }}
  />
);

export const Toggle = ({ checked, onChange }) => (
  <button
    onClick={() => onChange(!checked)}
    type="button"
    style={{
      width: 38,
      height: 22,
      borderRadius: 9999,
      border: 0,
      padding: 2,
      cursor: 'pointer',
      background: checked ? 'linear-gradient(135deg,#ff6900,#f54900)' : '#e5e7eb',
      transition: 'background 180ms ease-out',
      display: 'inline-flex',
      alignItems: 'center',
    }}
  >
    <span
      style={{
        display: 'block',
        width: 18,
        height: 18,
        borderRadius: 9999,
        background: '#fff',
        transform: checked ? 'translateX(16px)' : 'translateX(0)',
        transition: 'transform 180ms ease-out',
        boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
      }}
    />
  </button>
);
